import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import {
  enqueue,
  peekOutbox,
  utilityEvents as utilityEventsCache,
} from "../local/db";
import { syncEngine } from "../local/sync";

// Utility events (BLOOD / LAMENESS, card #139) — persisted on the Ride
// server-side, delivered cross-scribe by polling. Every scribe/judge terminal
// scoring a ride polls GET /ride/:rideId/utility-events on this interval
// while the sheet is open, so a flag raised at one judge position shows up at
// every other position scoring the same ride. Modeled on announcer-fe's
// useClassResults (visibility-aware poll + read-through offline cache).
export const UTILITY_EVENTS_POLL_MS = 4000;

/**
 * @param {string} rideId
 * @param {{ online?: boolean }} [opts]
 * @returns {{
 *   events: Array,
 *   error: Error|null,
 *   raise: (type: string) => Promise<void>,
 *   clear: (eventId: string) => Promise<void>,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useRideUtilityEvents(rideId, { online = true } = {}) {
  const scoreApi = useScoreApi();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  const onlineRef = useRef(online);
  onlineRef.current = online;

  // Monotonic request-generation counter (review fix, card #139): refresh()
  // can be in flight (poll interval, or a raise()/clear() triggering an
  // extra refresh) when a newer refresh() is issued — e.g. an interval tick
  // that started before a raise() lands can resolve after it, and a blind
  // setEvents(rows) from that stale response would momentarily blank a
  // just-raised flag. Every refresh() call captures the generation at start
  // and discards its own result if a newer call has since been issued.
  const refreshGenRef = useRef(0);

  // Raises whose optimistic entry is on screen but whose op is not yet durable
  // in the outbox (#3068). raise() sets the optimistic entry synchronously and
  // only THEN awaits enqueue(); a refresh() that reads the outbox inside that
  // window would merge nothing and blank the chip until the next refresh. So
  // withPendingRaises unions the outbox with this map, and raise() drops its
  // entry only once enqueue() has resolved (from then on the outbox has it).
  const unqueuedRaisesRef = useRef(new Map());

  // Pending outbox merge (card #3056): raise() appends an optimistic _pending
  // entry to React state only — it deliberately never writes it into
  // utilityEventsCache (that would be a second source of truth to reconcile
  // when the POST lands). So EVERY refresh() result — the online fetch, the
  // offline cache read, and the error-fallback cache read — must be unioned
  // with the still-pending raiseUtilityEvent ops for this ride, or the next
  // poll/online-flip refresh overwrites state and the queued flag vanishes
  // from the scribe's banner mid-ride. The outbox itself is the single source
  // of truth: once the POST drains the op, it's gone from peekOutbox() and
  // only the server row remains — except for the brief window where the op
  // is still queued but the server already returned the event (flush in
  // flight), which the dedupe below (match on opId or on a server row of the
  // same type raised since the op was queued) keeps from showing twice.
  const withPendingRaises = useCallback(async (rows, id) => {
    // Snapshot BEFORE the outbox read: an entry deleted after this point had
    // its enqueue() resolve first, so the read below sees its op instead.
    const unqueued = [...unqueuedRaisesRef.current.values()]
      .filter((u) => u.rideId === id)
      .map((u) => u.entry);
    const ops = await peekOutbox();
    const pending = ops.filter(
      (op) =>
        op.type === "raiseUtilityEvent" &&
        op.entityKey === id &&
        op.status !== "terminal",
    );
    if (!pending.length && !unqueued.length) return rows;
    const merged = [...rows];
    // Already shown: by id, or (flush in flight) by a server row of the same
    // type raised since the entry was queued. Applies equally to outbox ops
    // and to not-yet-enqueued entries, since a background flush can post an op
    // before raise()'s own enqueue() await has resolved (#3068).
    const isShown = (entryId, type, createdAt) =>
      merged.some(
        (e) =>
          e.id === entryId ||
          (e.type === type &&
            !e._pending &&
            Date.parse(e.raisedAt ?? "") >= Date.parse(createdAt ?? "")),
      );
    for (const op of pending) {
      const type = op.payload?.type;
      if (isShown(op.opId, type, op.createdAt)) continue;
      merged.push({
        id: op.opId,
        type,
        raisedByScribeId: null,
        raisedAt: op.createdAt,
        _pending: true,
      });
    }
    for (const entry of unqueued) {
      if (!isShown(entry.id, entry.type, entry.raisedAt)) merged.push(entry);
    }
    return merged;
  }, []);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      if (onlineRef.current) {
        const rows =
          unwrap(await apiRef.current.rides.listRideUtilityEvents(id)) || [];
        if (isStale()) return;
        // #3056 review round 1 — withPendingRaises is itself an await (it
        // reads the outbox), so a newer refresh() can be issued while it is
        // in flight. Re-check staleness AFTER it resolves, or this older
        // refresh's merge lands last and overwrites the newer result — the
        // exact race refreshGenRef exists to prevent, reintroduced one line
        // later. Every branch below does the same re-check.
        const merged = await withPendingRaises(rows, id);
        if (isStale()) return;
        setEvents(merged);
        setError(null);
        await utilityEventsCache.put({
          rideId: id,
          events: rows,
          updatedAt: new Date().toISOString(),
        });
      } else {
        const cached = await utilityEventsCache.get(id);
        if (isStale()) return;
        const merged = await withPendingRaises(cached?.events ?? [], id);
        if (isStale()) return;
        setEvents(merged);
      }
    } catch (err) {
      if (isStale()) return;
      // Network failed after a possible cache hit — fall back to the last
      // known state rather than blanking the banner.
      const cached = await utilityEventsCache.get(id);
      if (isStale()) return;
      if (cached) {
        const merged = await withPendingRaises(cached.events, id);
        if (isStale()) return;
        setEvents(merged);
        setError(null);
      } else {
        setError(err);
      }
    }
  }, [withPendingRaises]);

  useEffect(() => {
    if (!rideId) {
      setEvents([]);
      return undefined;
    }
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, UTILITY_EVENTS_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [rideId, refresh, online]);

  // Raise is a QUEUED write (card #139 AC: survives reload + an offline
  // period) — enqueued into the same IndexedDB outbox the score-save path
  // uses, never a direct fetch. An optimistic local entry renders
  // immediately (even offline); it's replaced by the server row once the op
  // drains and the next refresh() lands.
  const raise = useCallback(
    async (type) => {
      const id = rideIdRef.current;
      if (!id || !type) return;
      const opId = `utilityEvent:raise:${id}:${type}:${Date.now()}`;
      const optimistic = {
        id: opId,
        type,
        raisedByScribeId: null,
        raisedAt: new Date().toISOString(),
        _pending: true,
      };
      // Any refresh issued before this point may have read the outbox without
      // this op and can no longer be trusted: bump the generation so it is
      // discarded, and register the entry so a refresh issued from here until
      // enqueue() resolves still carries it (#3068).
      refreshGenRef.current += 1;
      unqueuedRaisesRef.current.set(opId, { rideId: id, entry: optimistic });
      setEvents((prev) => [...prev, optimistic]);
      try {
        await enqueue({
          opId,
          type: "raiseUtilityEvent",
          entityKey: id,
          payload: { rideId: id, type },
        });
      } finally {
        unqueuedRaisesRef.current.delete(opId);
      }
      if (syncEngine.isOnline()) {
        await syncEngine.flush();
        await refresh();
      } else {
        await syncEngine.refreshPending();
      }
    },
    [refresh],
  );

  // Clear is likewise queued. Optimistically removed locally; the server is
  // the authoritative gate (originator-or-admin, card #139) — if a stale/
  // unauthorized clear is rejected, the next refresh() brings it back.
  const clear = useCallback(
    async (eventId) => {
      const id = rideIdRef.current;
      if (!id || !eventId) return;
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      await enqueue({
        opId: `utilityEvent:clear:${id}:${eventId}`,
        type: "clearUtilityEvent",
        entityKey: id,
        payload: { rideId: id, eventId },
      });
      if (syncEngine.isOnline()) {
        await syncEngine.flush();
        await refresh();
      } else {
        await syncEngine.refreshPending();
      }
    },
    [refresh],
  );

  return { events, error, raise, clear, refresh };
}

export default useRideUtilityEvents;
